import type { CompositeChartSeries, LineChartSeries } from '@mantine/charts'

import { colors } from '@/configs/colors'
import type { AllConversationsPerCmEntry, DateRangeRequest, GetContentModuleResponse } from '@/services/api/api.types'
import { ContentModuleStatus } from '@/services/api/api.types'
import { isHelpModule } from '@/utils/contentModuleTables'

/**
 * Replace any '.', the graph library can't handle them in the labels
 */
export const sanitizeLabel = (label: string): string => {
  return label.replace(/\./g, '_')
}

/**
 * Format the date for x-axis labels in a chart
 */
export const formatChartDate = (date: string | null, showYear: boolean = false) => {
  if (!date) return ''

  // Split and treat as local date (year, monthIndex, day)
  const [year, month, day] = date.split('-').map(Number)
  const localDate = new Date(year, month - 1, day)

  const result = localDate.toLocaleDateString('en-US', {
    year: showYear ? 'numeric' : undefined,
    month: 'short',
    day: '2-digit',
  })

  return result
}

export const formatBillingDate = (date: string | null | undefined, fullDate: boolean = false): string => {
  if (!date) {
    return 'Date not available'
  }

  try {
    // Extract date part from timestamp (handle both 'YYYY-MM-DD' and 'YYYY-MM-DDTHH:mm:ss' formats)
    const datePart = date.split('T')[0]
    const [year, month, day] = datePart.split('-').map(Number)

    // Create date object in local timezone to avoid UTC conversion
    const localDate = new Date(year, month - 1, day)

    // Check if date is valid
    if (isNaN(localDate.getTime())) {
      return 'Invalid date'
    }

    return localDate.toLocaleDateString('en-US', {
      year: fullDate ? 'numeric' : undefined,
      month: fullDate ? 'long' : 'short',
      day: 'numeric',
    })
  } catch (error) {
    console.error('Error formatting date', error)
    return 'Invalid date'
  }
}

export const getRelativeDateRange = (daysAgo: number): DateRangeRequest => {
  return {
    date_from: `-${daysAgo}d`,
    date_to: null,
    explicit_date: false,
  }
}

const PERIOD_DAYS = 30

/**
 * Local calendar date as YYYY-MM-DD. Not `toISOString().split('T')[0]`, which converts a
 * local-midnight Date to UTC and so reports the previous day at UTC+ offsets.
 */
const toDateString = (date: Date): string => {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Every YYYY-MM-DD from startDate to endDate inclusive. Iterates in UTC so the day never
 * shifts under the caller's offset.
 */
export const eachDateStringInclusive = (startDate: string, endDate: string): string[] => {
  const dates: string[] = []
  const current = new Date(`${startDate}T00:00:00Z`)
  const end = new Date(`${endDate}T00:00:00Z`)

  while (current <= end) {
    dates.push(current.toISOString().split('T')[0])
    current.setUTCDate(current.getUTCDate() + 1)
  }

  return dates
}

export type ExplicitDateRange = {
  analyticsParams: DateRangeRequest
  billingParams: { startDate: string; endDate: string }
  startDate: string
  endDate: string
}

/**
 * Calculate explicit date ranges for a 30-day period offset by backPeriod.
 * backPeriod: 0 = current period (last 30 days), -1 = previous 30 days, etc.
 * Returns both DateRangeRequest format (for analytics APIs) and billing format.
 *
 * Pass createdAt to clamp the oldest period to the channel's creation date, so a channel
 * created mid-period still reaches its earliest data instead of that stretch being skipped.
 */
export const getExplicitDateRange = (backPeriod: number, createdAt?: string): ExplicitDateRange => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  // Calculate end date: for backPeriod 0, end is today; for -1, end is 30 days ago, etc.
  const endDate = new Date(today)
  endDate.setDate(endDate.getDate() + backPeriod * PERIOD_DAYS)

  // Calculate start date: 29 days before end date (30 day period inclusive)
  const startDate = new Date(endDate)
  startDate.setDate(startDate.getDate() - (PERIOD_DAYS - 1))

  const endDateStr = toDateString(endDate)
  const createdAtStr = createdAt?.split('T')[0]
  const periodStartStr = toDateString(startDate)
  // Compared as YYYY-MM-DD strings so the clamp never shifts under the viewer's offset.
  const startDateStr = createdAtStr && createdAtStr > periodStartStr ? createdAtStr : periodStartStr

  return {
    analyticsParams: {
      date_from: startDateStr,
      date_to: endDateStr,
      explicit_date: true,
    },
    // Billing rows are bucketed by UTC day, so span whole UTC days. Sending local midnight
    // instead left a 24h hole between consecutive periods, and truncated the final day, so
    // credits landing there appeared in no period at all.
    billingParams: {
      startDate: `${startDateStr}T00:00:00.000Z`,
      endDate: `${endDateStr}T23:59:59.999Z`,
    },
    startDate: startDateStr,
    endDate: endDateStr,
  }
}

/**
 * Check whether a date falls before a given creation date.
 */
export const isBeforeCreationDate = (date: string, createdAt: string | undefined): boolean => {
  if (!createdAt) return false
  const periodDate = new Date(date)
  const created = new Date(createdAt.split('T')[0])
  return periodDate < created
}

/**
 * Whether stepping back one more period would still land on data.
 *
 * Gated on the previous period's *end* date, not its start: a period that begins before the
 * channel existed but ends after it still holds real data, and clamping in
 * getExplicitDateRange makes that period show its true range.
 */
export const canNavigateToPreviousPeriod = (backPeriod: number, createdAt: string | undefined): boolean =>
  !isBeforeCreationDate(getExplicitDateRange(backPeriod - 1).endDate, createdAt)

export const createLineChartSeries = <T extends { label: string }>(items: T[]): LineChartSeries[] => {
  return items.map((item, index) => ({
    name: sanitizeLabel(item.label),
    color: colors.chartColors[index % colors.chartColors.length],
    label: item.label,
  }))
}

/**
 * Series for the conversations composite charts used in ContentModuleInsights and TopicInsights
 */
export const conversationsSeries: CompositeChartSeries[] = [
  {
    name: 'total',
    label: 'Total Conversations',
    color: colors.chartColors[0],
    type: 'line',
  },
  {
    name: 'unique',
    label: 'Unique User Conversations',
    color: colors.chartColors[1],
    type: 'line',
  },
  {
    name: 'allConversations',
    label: 'All Conversations',
    color: 'rgba(50, 204, 102, 0.25)',
    type: 'bar',
  },
]

// PostHog reports the sponsor's own (task-less) traffic under these breakdown values.
const sponsorTaskIds: Array<string | null> = [null, '$$_posthog_breakdown_null_$$']

export type CmConversationSplit = {
  published: AllConversationsPerCmEntry[]
  other: AllConversationsPerCmEntry[]
  stats: AllConversationsPerCmEntry[]
}

/**
 * Splits the per-CM conversation entries into the two Overview charts and the set the summary
 * tiles are totalled from. Help modules are Sidekick-managed and carry the triage traffic, so
 * they belong with the hidden "other" modules and out of the totals entirely (SME-1118).
 */
export const splitConversationsPerCm = (
  entries: AllConversationsPerCmEntry[],
  contentModules: GetContentModuleResponse[],
): CmConversationSplit => {
  if (!contentModules.length) return { published: entries, other: [], stats: entries }

  const taskIdsWhere = (predicate: (cm: GetContentModuleResponse) => boolean) =>
    new Set(
      contentModules
        .filter(predicate)
        .map(cm => cm.taskId)
        .filter(Boolean),
    )

  const publishedTaskIds = taskIdsWhere(cm => cm.status === ContentModuleStatus.Published && !isHelpModule(cm))
  const otherTaskIds = taskIdsWhere(cm => cm.status !== ContentModuleStatus.Published || isHelpModule(cm))
  const helpTaskIds = taskIdsWhere(isHelpModule)

  return {
    published: entries.filter(e => publishedTaskIds.has(e.taskId) || sponsorTaskIds.includes(e.taskId)),
    other: entries.filter(e => otherTaskIds.has(e.taskId)),
    stats: entries.filter(e => !helpTaskIds.has(e.taskId)),
  }
}

/**
 * Mirrors generate_conversation_stats in analytics_ms. The server's own stats block is summed
 * over every module including help ones, so the filtered totals have to be rebuilt here.
 */
export const totalConversationStats = (entries: AllConversationsPerCmEntry[]) => {
  const totalConversations = entries.reduce((sum, entry) => sum + entry.total, 0)
  const totalMessages = entries.reduce((sum, entry) => sum + entry.totalMessages, 0)

  return {
    moduleCount: entries.length,
    totalConversations,
    totalUniqueConversations: entries.reduce((sum, entry) => sum + entry.unique, 0),
    averageConversationLength: totalConversations ? totalMessages / totalConversations : 0,
  }
}
